import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { SquarePen } from "lucide-react";
import { updateTcs } from "pages/GenericOrderDetailsPage/apis";
import DeskDialog, { Field, inputClass } from "../DeskDialog";
import { formatInr } from "../../utils/money";

/**
 * Setting tax collected at source by hand. A reason is always asked for, because
 * it is a tax and the change is audited. Setting it below what the rules work
 * out needs a senior approver, and core refuses anyone else in so many words;
 * that sentence is shown here. "Back to the calculated amount" hands it back.
 */
export default function TCSEditDialog({
  tcs,
  override,
  disabled = false,
}: {
  tcs: number | string;
  override?: { amount: number; reason: string } | null;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const current = Number(tcs) || 0;

  const mutation = useMutation({
    mutationFn: updateTcs,
    onSuccess: (_result, variables) => {
      toast.success(variables.amount === null ? "TCS is back to the calculated amount" : "TCS updated");
      queryClient.invalidateQueries(["orderDetails"]);
      setOpen(false);
    },
    onError: (e: unknown) => {
      const message = e instanceof Error ? e.message : "The TCS could not be changed.";
      // A refusal for want of a senior approver says who to ask.
      setError(/senior/i.test(message) ? "Only a senior approver can set TCS below the calculated amount; ask your manager." : message);
    },
  });

  const reset = () => {
    setAmount("");
    setReason("");
    setError(null);
  };
  const typed = amount.trim() === "" ? null : Number(amount);
  const amountOk = typed !== null && Number.isFinite(typed) && typed >= 0;
  const reasonOk = reason.trim().length >= 3;
  const lowering = amountOk && typed! < current;

  return (
    <>
      <button
        type="button"
        aria-label="Edit TCS"
        disabled={disabled}
        onClick={() => {
          reset();
          setOpen(true);
        }}
        className="cursor-pointer rounded p-1 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#165FE3] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <SquarePen className="w-4 h-4 text-blue-700" />
      </button>
      <DeskDialog
        open={open}
        onOpenChange={setOpen}
        title="Edit TCS charges"
        description={
          override
            ? `Set by hand to ${formatInr(override.amount)}: ${override.reason}`
            : "The amount now on the order is the one the rules work out."
        }
        busy={mutation.isLoading}
        submitDisabled={!amountOk || !reasonOk}
        error={error}
        onSubmit={() => {
          setError(null);
          mutation.mutate({ amount: typed, reason });
        }}
      >
        <Field label="New TCS (INR)" htmlFor="tcs-amount" hint={`Now ${formatInr(current)}`}>
          <input
            id="tcs-amount"
            className={inputClass}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter new TCS charges"
          />
        </Field>
        {lowering && !error ? (
          <p className="text-xs text-[#7A5200]">
            This is less than the TCS now on the order. Only a senior approver can lower it.
          </p>
        ) : null}
        <Field label="Reason" htmlFor="tcs-reason" hint="At least 3 characters. It is kept with the change.">
          <textarea
            id="tcs-reason"
            className={`${inputClass} min-h-[72px]`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is TCS being changed?"
          />
        </Field>
        {override ? (
          <button
            type="button"
            disabled={!reasonOk || mutation.isLoading}
            onClick={() => {
              setError(null);
              mutation.mutate({ amount: null, reason });
            }}
            className="w-fit cursor-pointer text-sm font-medium text-[#165FE3] underline underline-offset-2 hover:text-[#1942B8] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back to the calculated amount
          </button>
        ) : null}
      </DeskDialog>
    </>
  );
}
